import { useState } from "react";
import { Link } from "react-router-dom";
import {
  LockClosedIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { Input } from "@hyperhedral/ui";
import { supabase } from "../lib/supabase";

type Stage = "form" | "success";

export function PinRecovery() {
  const [licenseKey, setLicenseKey] = useState("");
  const [challengeCode, setChallengeCode] = useState("");
  const [responseCode, setResponseCode] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("form");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke<{
        response_code?: string;
        error?: string;
      }>("pin-recovery", {
        body: {
          key: licenseKey.trim(),
          challenge_code: challengeCode.trim(),
        },
      });

      if (invokeError) {
        // Supabase wraps non-2xx responses as FunctionsHttpError — try to read the body.
        let bodyError: string | null = null;
        try {
          const ctx = (invokeError as { context?: { json?: () => Promise<{ error?: string }> } }).context;
          if (ctx?.json) {
            const body = await ctx.json();
            bodyError = body?.error ?? null;
          }
        } catch {
          // ignore; fall through to generic message
        }
        setError(bodyError ?? invokeError.message ?? "Something went wrong. Please try again.");
        return;
      }

      if (data?.error) {
        setError(data.error);
        return;
      }

      if (!data?.response_code) {
        setError("Unexpected response from the server. Please try again.");
        return;
      }

      setResponseCode(data.response_code);
      setStage("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleStartOver() {
    setLicenseKey("");
    setChallengeCode("");
    setResponseCode(null);
    setStage("form");
    setError(null);
  }

  return (
    <div className="account-page min-h-[calc(100vh-5rem)] py-12 md:py-16">
      <div className="mx-auto max-w-2xl px-6">
        <Link
          to="/help/faq#forgot-pin"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-bark-700 hover:text-forest-700"
        >
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          Back to help centre
        </Link>

        <div className="account-card mt-6 p-6 md:p-12">
          <div className="flex flex-col items-start gap-4 sm:flex-row">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-forest-600 text-cream-50">
              <LockClosedIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-bark-900">
                Reset your admin PIN
              </h1>
              <p className="mt-2 text-bark-700">
                Use this page when a member of staff has forgotten the BookBurrow admin PIN
                on the library computer. You'll need your licence key and the current
                challenge code shown in the app.
              </p>
            </div>
          </div>

          {stage === "form" && (
            <>
              <section className="mt-10 rounded-2xl bg-cream-100/80 p-6">
                <h2 className="font-display text-lg font-semibold text-bark-900">
                  Step 1 — Get a challenge code from BookBurrow
                </h2>
                <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-bark-700">
                  <li>
                    Open BookBurrow on the library computer.
                  </li>
                  <li>
                    On the PIN screen, click{" "}
                    <span className="font-semibold text-bark-900">Forgot PIN?</span>
                  </li>
                  <li>
                    BookBurrow will show a six-character code that looks like{" "}
                    <span className="rounded bg-cream-200 px-1.5 py-0.5 font-mono font-semibold text-bark-900">
                      FROG-4271
                    </span>
                    . Leave that screen open.
                  </li>
                </ol>
              </section>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <h2 className="font-display text-lg font-semibold text-bark-900">
                  Step 2 — Enter your details
                </h2>
                <Input
                  label="Licence key"
                  placeholder="BB-XXXX-XXXX-XXXX-XXXX"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                  required
                />
                <Input
                  label="Challenge code"
                  placeholder="e.g. FROG-4271"
                  value={challengeCode}
                  onChange={(e) => setChallengeCode(e.target.value.toUpperCase())}
                  autoComplete="off"
                  spellCheck={false}
                  required
                />

                {error && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl border border-gold-300 bg-gold-50 px-4 py-3 text-sm text-bark-900"
                  >
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gold-700" aria-hidden="true" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !licenseKey.trim() || !challengeCode.trim()}
                  className="w-full rounded-xl bg-forest-700 px-4 py-3 text-sm font-bold text-cream-50 shadow-lg transition-all hover:bg-forest-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Getting your response code…" : "Get response code"}
                </button>
              </form>

              <p className="mt-6 text-xs text-bark-600">
                Challenge codes are valid for one day only. If the code in BookBurrow changes
                while you're filling this in, just use the new one.
              </p>
            </>
          )}

          {stage === "success" && responseCode && (
            <div className="mt-10">
              <div className="flex items-center gap-3 rounded-2xl border border-forest-300 bg-forest-50 px-5 py-4">
                <ShieldCheckIcon className="h-6 w-6 flex-shrink-0 text-forest-700" aria-hidden="true" />
                <p className="text-sm font-medium text-forest-900">
                  Challenge verified. Use the code below to unlock PIN reset in BookBurrow.
                </p>
              </div>

              <section className="mt-8 rounded-2xl bg-cream-100/80 p-6">
                <h2 className="font-display text-lg font-semibold text-bark-900">
                  Step 3 — Enter this response code in BookBurrow
                </h2>
                <div className="paper-card mt-4 flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-forest-300 bg-cream-50 px-6 py-8">
                  <span className="text-xs font-semibold uppercase tracking-widest text-forest-700">
                    Response code
                  </span>
                  <span className="select-all font-mono text-3xl font-bold tracking-wider text-bark-900">
                    {responseCode}
                  </span>
                </div>
                <ol className="mt-6 list-decimal space-y-2 pl-5 text-sm text-bark-700">
                  <li>
                    Go back to the BookBurrow PIN recovery screen on the library computer.
                  </li>
                  <li>Type the response code above exactly as shown.</li>
                  <li>
                    BookBurrow will let you set a new admin PIN. Remember to write the new
                    PIN down somewhere safe.
                  </li>
                </ol>
              </section>

              <button
                type="button"
                onClick={handleStartOver}
                className="mt-8 w-full rounded-xl border border-forest-800/20 px-4 py-3 text-sm font-semibold text-forest-800 transition-all hover:border-forest-800/40 hover:bg-forest-50"
              >
                Start over
              </button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-bark-600">
          Having trouble?{" "}
          <a
            href="mailto:support@bookburrow.uk"
            className="font-medium text-forest-700 underline decoration-gold-400 decoration-2 underline-offset-4 hover:text-forest-800"
          >
            Email support@bookburrow.uk
          </a>
        </p>
      </div>
    </div>
  );
}
